import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subject, takeUntil } from 'rxjs';

import { IneligibleReasons } from '../../../../common/enums/ineligible-scenarios.enum';
import { IneligiblePageContent } from '../../../../common/interfaces/ineligible-page-content.interface';
import { content } from '../../../../content/zci/content';
import { environment } from '../../../../environments/environment';
import { APPLICATION_ROUTES } from '../../../constants/application-routes';
import { BreakpointObserverService } from '../../../services/breakpoint-observer/breakpoint-observer.service';
import { VgnService } from '../../../services/vgn/vgn.service';
import { ContentUtil } from '../../../utils/content/content.util';
import { AsyncPipe, NgIf } from '@angular/common';
import { ButtonModule } from '@vg-constellation/angular-20/button';
import {
    AdobeConfig,
    AdobeTag,
} from '../../../utils/decorators/dev/adobe-launch/adobe-launch.interfaces';
import { AdobeLaunchService } from '../../../services/adobe-launch/adobe-launch.service';
import { adobeConfigData } from './ineligible-page.component.adobe';
import { ClientSegmentFacadeService } from 'src/app/services/client-segment-observer/client-segment.service';
import { ClientSegmentContentDTO } from 'src/common/dtos/client-segment.dto';

@Component({
    selector: 'zci-ineligible-page',
    templateUrl: './ineligible-page.component.html',
    styleUrls: ['./ineligible-page.component.scss'],
    imports: [
        AsyncPipe,
        NgIf,
        ButtonModule,
    ],
})
export class IneligiblePageComponent implements OnInit, OnDestroy {
    private readonly vgnService = inject(VgnService);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly breakpointObserverService = inject(BreakpointObserverService);
    private adobeLaunchService = inject(AdobeLaunchService);
    private readonly clientSegmentFacadeService = inject(ClientSegmentFacadeService);

    content = ContentUtil.content || content;
    grantorContent = ContentUtil.grantorFragmentContent;

    isMobileView$: Observable<boolean> = this.breakpointObserverService.isMobileView$;

    ineligiblePageContent: IneligiblePageContent;

    isTrustOrOrgAccountType = false;

    agentAuthorizationFormUrl: string;

    adobeData: AdobeConfig;

    phoneNumber: string;

    pageContent: string;

    private destroy$ = new Subject<void>();

    ngOnInit(): void {
        this.vgnService.setMinimalNav(false);
        this.route.paramMap.subscribe((params) => {
            const ineligibleReason = params.get('id');
            if (Number(ineligibleReason) === IneligibleReasons.TRUST_OR_ORGANIZATION_ACCOUNT) {
                this.agentAuthorizationFormUrl = environment.AGENT_AUTHORIZATION_FORM_URL;
                this.isTrustOrOrgAccountType = true;
            }
            if (Number(ineligibleReason) === IneligibleReasons.SUBJECT_TO_B_NOTICE) {
                this.ineligiblePageContent = {
                    pageTitle: this.grantorContent.add_user.frmcSelectUserToGiveAccess.frmcAddPerson.errIneligibleScenarios.zciNoTinMatchErrorMessage.heading,
                    pageContent: this.grantorContent.add_user.frmcSelectUserToGiveAccess.frmcAddPerson.errIneligibleScenarios.zciNoTinMatchErrorMessage.text1,
                    buttonText: this.grantorContent.add_user.frmcSelectUserToGiveAccess.frmcAddPerson.errIneligibleScenarios.zciNoTinMatchErrorMessage.navReturnToAccessPermissions
                }
            } else {
                this.ineligiblePageContent = this.content.ineligibleScenarios[ineligibleReason];
            }
            this.adobeData = adobeConfigData[ineligibleReason];

            this.applyAdobeTagging('ngOnInit');
        });

        this.clientSegmentFacadeService.clientSegmentContent$
            .pipe(takeUntil(this.destroy$))
            .subscribe((content: ClientSegmentContentDTO) => {
                if (this.ineligiblePageContent?.pageContent) {
                    this.pageContent = this.ineligiblePageContent?.pageContent.replace('${phoneNumber}', content?.phoneNumber)
                }
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    goBackToAccessPermissions() {
        this.applyAdobeTagging('goBackToAccessPermissions');
        this.router.navigate([APPLICATION_ROUTES.OWN_ACCOUNTS]);
    }

    applyAdobeTagging(key: keyof this) {
        if (!this.adobeData) {
            return;
        }
        const tagData: AdobeTag[] = this.adobeData[key as string];
        if (!tagData) {
            return;
        }
        tagData.forEach((t: AdobeTag) =>
            this.adobeLaunchService[t.method].apply(this.adobeLaunchService, t.data),
        );
    }
}
